import React, { useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useForm, Controller } from 'react-hook-form';
import { trim, uniqueId } from 'lodash';
import {
	SdfAlert,
	SdfBox,
	SdfBusyIndicator,
	SdfButton,
	SdfExpandableBox,
	SdfIcon,
	SdfInput,
	SdfShimmer,
	SdfSwitch,
	SdfTooltip
} from '@synerg/react-components';
import { useApplicationState } from '../../../utilities/applicationState';

export function ProdCredentials({ clientType, prodClientId = '', prodClientSecret = '', showSecret = false, prodToggleShowSecret, clientSecretLoading, step }) {
	const [isStepOpen, setIsStepOpen] = useState({ 0: true, 1: true });

	const onStepChanged = (step, status) => {
		setIsStepOpen((s) => ({
			...s,
			...({ [step]: status === "open" })
		}));
	};

	return (
		<SdfExpandableBox
			variant="bordered"
			triggerPlacement="after"
			stroke="light"
			opened={isStepOpen[step]}
			onSdfClose={() => { onStepChanged(step, "close") }}
			onSdfOpen={() => { onStepChanged(step, "open") }}
			className="mb-8 mt-6"
		>
			<div className="text-lg font-bold" slot="header" style={{ letterSpacing: '-0.7px' }}>
				{clientType === "B2b" && "Data connector credentials"}
				{clientType === "B2c" && "End-user/SSO credentials"}
			</div>
			<SdfBox variant="filled" spacing="loose" className="mt-4">
				<div className="flex flex-wrap justify-between items-center mt-2 gap-x-4">
					<div className="text-heading-02 font-bold">
						ClientID
					</div>
					<div className="flex items-center">
						<div className="text-body-02 mr-6">
							{prodClientId}
						</div>
						{prodClientId ?
							<CopyToClipboard text={prodClientId}>
								<SdfTooltip positionContext="ELEMENT" tooltipId={uniqueId} trigger="click" state="info">
									<SdfButton emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
									Copied
								</SdfTooltip>
							</CopyToClipboard>
							:
							<SdfButton disabled={true} emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
						}
					</div>
				</div>

				<div className="flex flex-wrap justify-between items-center mt-6 gap-x-4">
					<div className="text-heading-02 font-bold">
						Client secret
					</div>
					<div className="flex items-center">
						<div className="flex items-center mr-10">
							<SdfSwitch
								checked={showSecret}
								onClick={(event) => {
									prodToggleShowSecret(event.target.checked, clientType)
								}}
							/>
							<span slot="label" className="text-md text-action-primary font-medium mr-1">Reveal </span>
						</div>
						{clientSecretLoading ? <SdfShimmer style={{ width: "300px" }} /> :
							<>
								{prodClientSecret ? <div className="text-body-02 mr-6">
									{prodClientSecret}
								</div> : <div className="text-body-02 mr-6 pt-1">**********************</div>}
								{prodClientSecret ?
									<CopyToClipboard text={prodClientSecret}>
										<SdfTooltip positionContext="ELEMENT" tooltipId={uniqueId} trigger="click" state="info">
											<SdfButton emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
											Copied
										</SdfTooltip>
									</CopyToClipboard>
									:
									<SdfButton disabled={true} emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
								}
							</>}
					</div>
				</div>

				<div className="flex flex-wrap justify-between items-center mt-6 mb-5 gap-x-4">
					<div className="text-heading-02 font-bold">
						Grant type
					</div>
					<div className="flex items-center">
						<div className="text-body-02 mr-24">
							{clientType === "B2b" && "client_credentials"}
							{clientType === "B2c" && "auth code"}
						</div>
					</div>
				</div>
			</SdfBox>
		</SdfExpandableBox >
	);
};

export function ApplicationIds({ appType, appId = '' }) {
	return (
		<SdfBox variant="bordered" className="mt-5">
			<div className="flex justify-between items-center">
				<div className="text-heading-02 font-bold">
					{appType === "dataConnector" && "Data connector ID"}
					{appType === "endUser" && "End-user/SSO ID"}
				</div>
				<div className="flex items-center">
					<div className="text-body-02 mr-6">
						{appId}
					</div>
					{appId ?
						<CopyToClipboard text={appId}>
							<SdfTooltip positionContext="ELEMENT" tooltipId={uniqueId} trigger="click" state="info">
								<SdfButton emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
								Copied
							</SdfTooltip>
						</CopyToClipboard>
						:
						<SdfButton disabled={true} emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
					}
				</div>
			</div>
		</SdfBox>
	);
};


export default function MarketplaceListing({ editMode, submit = {} }) {
	// eslint-disable-next-line no-unused-vars
	const [{ projects: { current: project } }, dispatch] = useApplicationState();
	const [isEditMode, setEditMode] = useState(editMode);
	const urlList = project.appListingUrls || [];
	const { unregister, control, handleSubmit, setValue } = useForm();
	const { onSubmit, isSaving, status, numberOfUrls } = submit;
	const [fieldList, setFieldList] = useState([]);

	const ShowError = ({ message }) => {
		return (
			<SdfAlert slot="alert" size="sm" status="error" type="inline">
				{message}
			</SdfAlert>
		);
	};

	const getAppListingValidations = (field, value) => {
		let errorMessage;
		if (trim(value).length === 0) {
			errorMessage = `${field} cannot be empty`;
		}

		const isValidUrl = () => {
			try {
				return new URL(value);
			} catch (_) {
				return 'Invalid URL';
			}
		}
		if (field === 'URL') {
			if (!errorMessage) {
				errorMessage = isValidUrl();
			}
		}
		return errorMessage;
	};

	const addAppListingField = () => {
		const newFieldId = Math.random().toString(36).substring(2);
		const list = [...fieldList, {
			nameId: `listing-name-${newFieldId}`,
			urlId: `listing-url-${newFieldId}`,
			name: '',
			url: ''
		}];
		setFieldList(list);
	};

	const removeAppListingField = (nameId, urlId) => {
		const list = fieldList.filter(url => url.nameId !== urlId && urlId !== url.urlId);
		unregister(nameId);
		unregister(urlId);
		setFieldList(list);
	};

	// HOOKS

	useEffect(() => {
		setFieldList(Array.isArray(urlList) && urlList.length > 0 ? urlList : [{
			nameId: 'listing-name-0',
			urlId: 'listing-url-0'
		}]);

		urlList.forEach(url => {
			if (url.nameId && url.name) {
				setValue(url.nameId, url.name);
			}
			if (url.urlId && url.url) {
				setValue(url.urlId, url.url);
			}
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => void setEditMode(editMode), [editMode]);


	return (
		<div className="mt-6">
			<SdfBox variant="bordered" spacing="loose" className="px-8">
				<SdfAlert status="info" className="flex justify-center">
					<div className="text-md">Once app listings are submitted, the partner production project will be marked as LIVE</div>
				</SdfAlert>
				<div className="my-8 flex justify-between">
					<div className="text-heading-02 font-bold">Please enter Marketplace listing URL for the partner after publishing
					</div>
					<div className={`success-button ${(!isEditMode && 'disabled')}`} onClick={() => addAppListingField()}>
						<SdfIcon icon="action-add" className="mr-1" />
						Add new app listings
					</div>
				</div>
				<div>
					{fieldList.map((url, index) => <div key={`${url.nameId}-${url.urlId}`} className="app-listing-url flex items-start mt-4 col-gap-4">
						<Controller
							name={url.nameId}
							control={control}
							rules={{
								validate: value => getAppListingValidations('Name', value)
							}}
							render={({
								field: { name, value, onChange },
								fieldState: { error },
							}) => {
								return (
									<SdfInput
										name={name}
										label={`App listing name ${index + 1}`}
										className="mr-3 w-3/4"
										{...(!isEditMode) && { disabled: true }}
										onSdfChange={(event) => {
											onChange(event.detail);
										}}
										value={value}
										state={error ? 'error' : 'normal'}
									>
										{error && error.message &&
											<ShowError message={error.message} />
										}
									</SdfInput>);
							}} />

						<Controller
							name={url.urlId}
							control={control}
							rules={{
								validate: value => getAppListingValidations('URL', value)
							}}
							render={({
								field: { name, value, onChange },
								fieldState: { error },
							}) => (
								<SdfInput
									name={name}
									label={`App listing URL ${index + 1}`}
									className="mr-3 w-3/4"
									{...(!isEditMode) && { disabled: true }}
									onSdfChange={(event) => {
										onChange(event.detail);
									}}
									value={value}
									state={error ? 'error' : 'normal'}
								>
									{error && error.message &&
										<ShowError message={error.message} />
									}
								</SdfInput>)
							} />

						<div className={`error-button flex mt-8 font-medium ${(!isEditMode || fieldList.length < 2) && 'disabled'}`} onClick={() => removeAppListingField(url.nameId, url.urlId)}>
							<SdfIcon icon="action-delete" className="mr-1" />
							Delete
						</div>
					</div>)}
				</div>

				{onSubmit && <div className="mt-6 flex items-center">
					<SdfButton
						type="submit"
						emphasis="primary"
						onClick={isEditMode ? handleSubmit((data) => { onSubmit(data); setEditMode(false) }) : () => setEditMode(true)}
						{...(isSaving && !isEditMode) && { disabled: true }}
					>
						<div style={{ height: '1.625rem' }}>
							{isEditMode && 'Save'}
							{(!isEditMode && !isSaving) && 'Edit'}
							{(!isEditMode && isSaving) && 'Saving'}
							{isSaving && <SdfBusyIndicator className='relative' style={{ bottom: '1.25rem' }} size='sm' />}
						</div>
					</SdfButton>

					{status === 'error' && <SdfAlert type="inline" status={status} className="ml-10">Failed to submit! Please try again. </SdfAlert>}

					{status === 'success' &&
						<SdfAlert type="inline" status={status} className="ml-10">
							{numberOfUrls <= 1 && "App Listing URL is submitted!"}
							{numberOfUrls > 1 && "App Listing URLs are submitted!"}
						</SdfAlert>
					}
				</div>}
			</SdfBox>
		</div>
	);
}
